import React from "react";
import { ListMusic, X, ChevronUp, ChevronDown, Trash2 } from "lucide-react";

/**
 * SetlistPanel — visible queue of tracks the studio will auto-advance
 * through when SetList Lock is armed. Each row has reorder + remove
 * controls. The first item is the next-up after the current sweep.
 *
 * Empty state explains how to use it (drop tracks via the "+" on each
 * row in the Track Browser below).
 */
export default function SetlistPanel({
  setlist = [],
  setlistLock = false,
  onToggleLock,
  onMove,
  onRemove,
  onClear,
}) {
  return (
    <section data-testid="radio-host-setlist-panel" className="rounded-xl border border-white/10 bg-black/40 p-3 mb-4">
      <header className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <ListMusic className="w-4 h-4 text-emerald-300 shrink-0" />
          <h3 className="text-[11px] uppercase tracking-[0.25em] font-black text-white/85">SetList</h3>
          <span className="text-[10px] text-white/45">{setlist.length} queued</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            data-testid="radio-host-setlist-lock"
            onClick={onToggleLock}
            aria-pressed={setlistLock}
            title={setlistLock ? "SetList Lock armed — studio auto-advances after each sweep. Click to disarm." : "Arm SetList Lock — the studio will auto-load the next track from the queue after every sweep."}
            className={`text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded border transition ${
              setlistLock
                ? "bg-emerald-500/30 text-emerald-100 border-emerald-400/60"
                : "bg-white/5 text-white/65 border-white/10 hover:bg-white/10"
            }`}
          >
            {setlistLock ? "Lock • ARMED" : "Lock"}
          </button>
          {setlist.length > 0 && (
            <button
              data-testid="radio-host-setlist-clear"
              onClick={onClear}
              title="Clear queue"
              className="text-white/40 hover:text-rose-300 transition p-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </header>
      {setlist.length === 0 ? (
        <div className="text-[11px] text-white/45 leading-snug py-1">
          Queue empty. Tap the <span className="font-bold text-emerald-300">+</span> on any track in the browser below to add it. When <span className="font-bold text-emerald-300">SetList Lock</span> is armed, the studio auto-advances after each sweep.
        </div>
      ) : (
        <ol data-testid="radio-host-setlist-items" className="space-y-1 max-h-[170px] overflow-y-auto pr-1">
          {setlist.map((t, i) => (
            <li
              key={t.id || i}
              data-testid={`radio-host-setlist-item-${i}`}
              className="flex items-center gap-2 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-[11px]"
            >
              <span className="font-mono font-black text-white/40 w-4 text-center">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-white truncate">{t.title || t.name || t.id}</div>
                {t.artist && <div className="text-white/45 truncate text-[10px]">{t.artist}</div>}
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                <button
                  onClick={() => onMove(t.id, -1)}
                  disabled={i === 0}
                  title="Move up"
                  className="text-white/40 hover:text-white disabled:opacity-30 p-1"
                  aria-label="Move up"
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => onMove(t.id, 1)}
                  disabled={i === setlist.length - 1}
                  title="Move down"
                  className="text-white/40 hover:text-white disabled:opacity-30 p-1"
                  aria-label="Move down"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => onRemove(t.id)}
                  title="Remove"
                  className="text-white/40 hover:text-rose-300 p-1"
                  aria-label="Remove from queue"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
